import React from "react";
import { useGetBusinesses } from "@/hooks/useGetBusinesses";
import { AllBusiness, isAllBusiness } from "@/api";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { OnboardingProgressIndication } from "./OnboardingProgressIndication";
import { LoadingScreen } from "@/LoadingScreen";
import * as Sentry from "@sentry/react";
import { BusinessStatus, useBusinessesStatusGetter } from "@/hooks/useGetBusinessStatus";
import { ErrorDoodle } from "@/components/ErrorDoodle";
import { LogoutButton } from "@/components/LogoutButton";
import { useAuth } from "@/Auth";

export function BusinessContextGuard(props: React.PropsWithChildren) {
  const businesses = useGetBusinesses();
  const {
    business: businessContext,
    isLoading: isBusinessContextLoading,
    setBusiness: setBusinessContext,
  } = useBusinessContext();
  const getBusinessStatus = useBusinessesStatusGetter();
  const auth = useAuth();
  const isDemoUser = auth.user?.is_demo;

  const [globalStatus, setGlobalStatus] = React.useState<
    "error" | "onboarding" | "unlicensed" | "undetermined" | "ok"
  >("undetermined");

  const businessStatuses = React.useMemo(() => {
    return businesses.data?.map((b) => getBusinessStatus(b)) ?? [];
  }, [businesses.data, getBusinessStatus]);

  const allBusinessStatus = React.useMemo(() => {
    return getBusinessStatus(AllBusiness);
  }, [getBusinessStatus]);

  React.useEffect(() => {
    if (businesses.isError) {
      setGlobalStatus("error");
      return;
    }
    if (isBusinessContextLoading) {
      setGlobalStatus("undetermined");
      return;
    }
    if (!businesses.isSuccess) {
      setGlobalStatus("undetermined");
      return;
    }

    if (businesses.data.length === 0) {
      Sentry.captureEvent({
        message: "No businesses found",
        level: "error",
      });
      setGlobalStatus("error");
      return;
    }

    if (isDemoUser) {
      setGlobalStatus("ok");
      return;
    }

    const statusDistribution = businessStatuses.reduce(
      (acc, status) => {
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      {} as Record<BusinessStatus, number>,
    );

    if (statusDistribution["undetermined"] > 0) {
      setGlobalStatus("undetermined");
      return;
    }

    if (statusDistribution["unlicensed"] === businessStatuses.length) {
      setGlobalStatus("unlicensed");
      return;
    }

    if (
      (statusDistribution["onboarding"] || 0) +
        (statusDistribution["installation_failed"] || 0) +
        (statusDistribution["status_error"] || 0) ===
      businessStatuses.length
    ) {
      setGlobalStatus("onboarding");
      return;
    }

    if (!statusDistribution["available"]) {
      setGlobalStatus(
        statusDistribution["onboarding"] ||
          statusDistribution["installation_failed"] ||
          statusDistribution["status_error"]
          ? "onboarding"
          : "unlicensed",
      );
      return;
    }

    function selectBusiness(businessId: string) {
      // Rechecking a saved selection must not overwrite a newer selection in another tab.
      if (businessContext!.id !== businessId) setBusinessContext(businessId);
      setGlobalStatus("ok");
    }

    function selectFirstAvailableBusiness() {
      const firstAvailableBusinessIndex = businessStatuses.findIndex(
        (status) => status === "available",
      );
      // we have already checked that there is at least one available business
      selectBusiness(businesses.data![firstAvailableBusinessIndex].id);
    }

    if (isAllBusiness(businessContext.id)) {
      if (allBusinessStatus === "available") {
        selectBusiness(AllBusiness.id);
        return;
      } else {
        selectFirstAvailableBusiness();
        return;
      }
    }

    const businessContextIndex = businesses.data.findIndex((b) => b.id === businessContext.id);
    if (businessContextIndex !== -1) {
      const businessContextStatus = businessStatuses[businessContextIndex];
      if (businessContextStatus === "available") {
        selectBusiness(businessContext.id);
      } else {
        selectFirstAvailableBusiness();
      }
      return;
    }

    Sentry.captureEvent({
      message: "Couldn't find the business context id inside the businesses list",
      level: "warning",
      extra: {
        businesses: businesses.data,
        persistedBusinessContext: businessContext.id,
      },
    });
    selectFirstAvailableBusiness();
    return;
  }, [
    businesses.isSuccess,
    businesses.data,
    businesses.isError,
    isBusinessContextLoading,
    businessStatuses,
    isDemoUser,
    businessContext,
    allBusinessStatus,
    setBusinessContext,
    setGlobalStatus,
  ]);

  if (globalStatus === "undetermined") {
    return <LoadingScreen />;
  }

  if (globalStatus === "error") {
    return <Error />;
  }

  if (globalStatus === "unlicensed") {
    return <LicenseRequired />;
  }

  if (globalStatus === "onboarding") {
    return <OnboardingProgressIndication />;
  }

  return props.children;
}

function LicenseRequired() {
  const businesses = useGetBusinesses();

  return (
    <div className="flex flex-col items-center min-h-screen bg-background-gray py-4">
      <span className="text-feint-gray">
        Businesses:{" "}
        {businesses.data
          ?.map((business) => `${business.display_name} (${business.ref})`)
          .join(", ")}
      </span>

      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="flex flex-col items-center">
          <ErrorDoodle />
          <div className="font-medium text-[32px] text-center">BI Addon/License Required</div>
        </div>
        <div className="text-feintGray font-medium text-center w-[50%]">
          Your Business doesn't have the necessary "Addon/License" to continue using Foodics BI.
          Please contact your account manager to extend your free trial or enable the addon.
        </div>
        <LogoutButton preset="bordered" className="mt-4" />
      </div>
    </div>
  );
}

function Error() {
  return (
    <div className="bg-backgroundGray h-screen v-screen flex justify-center items-center">
      <span className="text-lg font-medium">
        An error has occurred, try refreshing the page or contact support.
      </span>
    </div>
  );
}
